import { useContext } from "react";
import { createFailure, ensure, validationFailed, ValidationResult, validationSuccessful } from "sprint-common-web";

import { CreateEntityResponse } from "../../../../../../common/api/common/CreateEntityResponse";
import { CreateSeasonRequest } from "../../../../api/CreateSeasonRequest";
import { useAssetsManageTvSeriesApi } from "../../../../api/useAssetsManageTvSeriesApi";
import { TvSeasonDetails } from "../../../../TvSeriesDetails";
import { TvSeriesContext } from "../../../context/TvSeriesContext";
import { CreateSeasonFormInputs } from "./CreateSeasonFormInputs";

export const useCreateSeasonForm = () => {
    const { tvSeries } = useContext(TvSeriesContext);
    ensure(tvSeries);

    const { createSeason } = useAssetsManageTvSeriesApi();

    const createNewSeason = async (inputs: CreateSeasonFormInputs): Promise<TvSeasonDetails> => {
        return createSeason(tvSeries.id, createRequest(inputs)).then((response: CreateEntityResponse) => {
            return createNewSeasonDetails(response.entityId, inputs);
        });
    };

    const createNewSeasonDetails = (newId: string, inputs: CreateSeasonFormInputs): TvSeasonDetails => {
        ensure(inputs.name);
        ensure(inputs.number);

        return {
            id: newId,
            isModified: true,
            name: inputs.name,
            episodes: [],
            number: inputs.number
        };
    };

    const isSeasonNameUnique = (name: string): ValidationResult => {
        const isUnique = !tvSeries.seasons.some(
            (season: TvSeasonDetails) => season.name.toLowerCase() === name?.toLowerCase()
        );

        if (isUnique) {
            return validationSuccessful();
        }

        const failure = createFailure(
            "The season name is already taken. Please enter a different name",
            "season.TitleAlreadyTaken"
        );

        return validationFailed([failure]);
    };

    const isNewSeasonNumberUnique = (number: number): ValidationResult => {
        const isUnique = !tvSeries.seasons.some(
            (season: TvSeasonDetails) => season.number === number && !season.mergedTargetId
        );

        if (isUnique) {
            return validationSuccessful();
        }

        const failure = createFailure(
            "The season number is already taken. Please enter a different number",
            "season.NumberAlreadyTaken"
        );

        return validationFailed([failure]);
    };

    return {
        createNewSeason,
        isSeasonNameUnique,
        isNewSeasonNumberUnique
    };
};

const createRequest = (inputs: CreateSeasonFormInputs): CreateSeasonRequest => {
    ensure(inputs.name);
    ensure(inputs.number);

    return {
        name: inputs.name,
        number: inputs.number
    };
};
